import React, { useCallback, useState } from 'react';

import { link } from '@aline/routes';
import { Link, Paper, Skeleton, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { format, parseISO } from 'date-fns';
import { ChevronRight, Circle, CircleCheck, ExternalLink } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useEvent } from 'react-use';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { Spacer } from 'src/components/generic/Spacer';
import { useIsOrgAdmin } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import TeamsIconMarkup from 'src/images/integrations/microsoft-teams.svg';
import { makeStyles } from 'tss-react/mui';

import {
  IntegrationActionButton,
  integrationHoverActionRevealStyles,
} from './IntegrationActionButton';
import { integrationCardStyles } from './IntegrationRow';

const CHECKLIST_ICON_SIZE = 13;

const SLACK_ICON_URL = 'https://a.slack-edge.com/80588/marketing/img/meta/slack_hash_256.png';
const TEAMS_ICON_URL = `data:image/svg+xml,${encodeURIComponent(TeamsIconMarkup)}`;
const MANAGE_CHANNELS_ADMIN_TOOLTIP = 'Only organization admins can manage channel integrations';
const VIEW_CHANNELS_ADMIN_HINT = 'Only organization admins can view connection details';

/**
 * How the install CTA behaves for a provider: an unavailable placeholder, a GET
 * link that navigates to the server install route. Carried on the display so
 * RowAction reads the action off an exhaustive Record — adding a provider is a
 * compile error until it declares one, rather than silently falling through to a
 * Slack URL.
 */
type ProviderInstallCta = { kind: 'comingSoon' } | { href: string; kind: 'link' };

/** Provider-specific display chrome consumed by the provider-agnostic row. */
interface ProviderDisplay {
  /** What one connection of this provider is called, in the user's words. */
  readonly connectionNoun: string;
  readonly emptyHint: string;
  readonly iconUrl: string;
  readonly installCta: ProviderInstallCta;
  readonly installLabel: string;
  readonly label: string;
  /**
   * Shown when the pending step has no link to offer. Graph can omit a Teams app
   * while it propagates, when tenant policy hides it, or when an organization
   * package has not been uploaded. Older consents can also lack the application
   * permission required for the lookup.
   */
  readonly manualInstallHint: string | null;
  /**
   * What stops working once this provider is disconnected.
   *
   * Present only where the consequence is actually known — stating a specific
   * loss the product may not have is worse than saying nothing.
   */
  readonly disconnectDetail?: string;
}

const PROVIDER_DISPLAY: Record<Apollo.ChannelProvider, ProviderDisplay> = {
  [Apollo.ChannelProvider.Slack]: {
    connectionNoun: 'workspace',
    /* The exact inverse of `emptyHint`: what connecting bought, undone. */
    disconnectDetail: 'Mentioning @Aline in its channels will no longer start a thread.',
    emptyHint: 'Mention @Aline in any channel to start a thread.',
    iconUrl: SLACK_ICON_URL,
    installCta: { href: buildChannelInstallPath('slack'), kind: 'link' },
    installLabel: 'Add to Slack',
    label: 'Slack',
    manualInstallHint: null,
  },
  [Apollo.ChannelProvider.Resend]: {
    connectionNoun: 'account',
    emptyHint: 'Email channel — set up sender domain verification.',
    iconUrl: '',
    installCta: { kind: 'comingSoon' },
    installLabel: 'Connect email',
    label: 'Email',
    manualInstallHint: null,
  },
  [Apollo.ChannelProvider.Teams]: {
    connectionNoun: 'workspace',
    emptyHint:
      'Requires a Microsoft 365 admin. Afterwards, mention @Aline in any Teams channel to start a thread.',
    iconUrl: TEAMS_ICON_URL,
    installCta: { href: buildChannelInstallPath('teams'), kind: 'link' },
    installLabel: 'Add to Teams',
    label: 'Microsoft Teams',
    manualInstallHint:
      'Setup is not finished. An Aline admin can retry setup without disconnecting. ' +
      'If setup still cannot finish, ask a Teams admin to check app availability and permissions.',
  },
};

const useStyles = makeStyles()(theme => ({
  action: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  actionButtons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  checklist: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  checklistDone: {
    color: theme.palette.success.main,
  },
  checklistDoneItem: {
    alignItems: 'center',
    color: theme.palette.success.main,
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  checklistHint: {
    // Align with the checklist text rather than the bullet it explains.
    paddingLeft: `calc(${CHECKLIST_ICON_SIZE}px + ${theme.spacing(0.5)})`,
  },
  checklistItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  checklistLink: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.5),
  },
  checklistTodo: {
    color: theme.palette.text.disabled,
  },
  description: {
    flexGrow: 1,
    margin: theme.spacing(0.25, 1),
  },
  display: {
    alignItems: 'center',
    display: 'flex',
  },
  icon: {
    display: 'block',
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  iconBox: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexShrink: 0,
    height: theme.spacing(5),
    justifyContent: 'center',
    position: 'relative',
    width: theme.spacing(5),
  },
  paper: integrationCardStyles(theme),
  row: {
    ...integrationHoverActionRevealStyles,
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(1.5),
  },
}));

interface ChannelIntegrationRowProps {
  provider: Apollo.ChannelProvider;
}

export function ChannelIntegrationRow({ provider }: ChannelIntegrationRowProps): JSX.Element {
  const { classes } = useStyles();
  const { isOrgAdmin, loading: userLoading } = useIsOrgAdmin();
  /**
   * `channelInstallations` is admin-only on the server, so running it for anyone
   * else is a guaranteed `UnauthorizedError`. Skip until the current user
   * resolves, then skip for non-admins — `userLoading` is folded into `loading`
   * below so the row holds on a skeleton rather than flashing an unconnected
   * state at an admin whose role has not arrived yet.
   */
  const {
    data,
    loading: installsLoading,
    refetch,
  } = Apollo.useGetChannelInstallationsQuery({
    skip: userLoading || !isOrgAdmin,
  });
  const loading = userLoading || (installsLoading && !data);
  const installs =
    data?.channelInstallations.filter(installation => installation.provider === provider) ?? [];

  /**
   * A pending install is completed in another tab and confirmed by a webhook, so
   * nothing tells this page when it lands. Refetching when the user comes back
   * turns that into "it says Connected now" instead of a manual reload. Gated on
   * there being something to wait for so a settled page stays quiet.
   */
  const shouldRefreshOnFocus = isOrgAdmin && installs.some(getIsAwaitingProvider);
  useEvent('focus', () => {
    if (!shouldRefreshOnFocus) return;
    void refetch();
  });

  const display = PROVIDER_DISPLAY[provider];
  const rowInstalls = loading || installs.length === 0 ? [null] : installs;

  return (
    <Paper className={classes.paper} elevation={0}>
      {rowInstalls.map(install => (
        <div key={install?.id ?? provider} className={classes.row}>
          <div className={classes.display}>
            {Boolean(display.iconUrl) && (
              <div className={classes.iconBox}>
                <img alt={display.label} className={classes.icon} src={display.iconUrl} />
              </div>
            )}
            <div className={classes.description}>
              <Typography variant="subtitle2">{display.label}</Typography>
              <StatusLine
                display={display}
                finishInstallUrl={install?.appInstallUrl ?? null}
                install={install}
                isOrgAdmin={isOrgAdmin}
                loading={loading}
              />
            </div>
          </div>
          <Spacer />
          <RowAction
            display={display}
            finishInstallUrl={install?.appInstallUrl ?? null}
            install={install}
            isOrgAdmin={isOrgAdmin}
            loading={loading}
          />
        </div>
      ))}
    </Paper>
  );
}

type ChannelInstallationView = Pick<
  Apollo.ChannelInstallation,
  | 'id'
  | 'provider'
  | 'externalAccountId'
  | 'displayName'
  | 'installedAt'
  | 'status'
  | 'createdAt'
  | 'appInstallUrl'
>;

function getIsAwaitingProvider(install: ChannelInstallationView) {
  return install.status === Apollo.ChannelInstallationStatus.ActionNeeded;
}

interface StatusLineProps {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView | null;
  isOrgAdmin: boolean;
  loading: boolean;
}

function StatusLine({ display, finishInstallUrl, install, isOrgAdmin, loading }: StatusLineProps) {
  if (loading) return <Skeleton variant="text" width={220} />;
  /**
   * The admin-only query is skipped for everyone else, so `install` is null
   * whether or not the provider is connected. Falling through to the empty hint
   * would state the integration is not set up when it may well be — say what we
   * actually know instead.
   */
  if (!isOrgAdmin) {
    return (
      <Typography color="secondary" variant="caption">
        {VIEW_CHANNELS_ADMIN_HINT}
      </Typography>
    );
  }
  if (!install) {
    return (
      <Typography color="secondary" variant="caption">
        {display.emptyHint}
      </Typography>
    );
  }
  if (getIsAwaitingProvider(install))
    return <PendingChecklist display={display} finishInstallUrl={finishInstallUrl} />;

  const installedAtIso = install.installedAt ?? install.createdAt;
  const installedAt = installedAtIso ? format(parseISO(installedAtIso), 'MMM d, yyyy') : null;
  return (
    <Typography color="secondary" variant="caption">
      Connected to <strong>{getInstallDisplayName(install)}</strong>
      {installedAt ? ` on ${installedAt}` : null}
    </Typography>
  );
}

/**
 * The install is linked on our side but the app still has to be added in the
 * provider, which only the user can do. Spelling both steps out is what keeps
 * this from reading as a failure — the first step genuinely succeeded, and the
 * row flips to connected on its own once the provider reports the app installed.
 */
function PendingChecklist({
  display,
  finishInstallUrl,
}: {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
}) {
  const { classes } = useStyles();
  const finishLabel = `Add the Aline app in ${display.label} to finish`;
  return (
    <div className={classes.checklist}>
      <Typography className={classes.checklistDoneItem} variant="caption">
        <CircleCheck className={classes.checklistDone} size={CHECKLIST_ICON_SIZE} />
        Linked to this organization
      </Typography>
      <Typography className={classes.checklistItem} color="secondary" variant="caption">
        <Circle className={classes.checklistTodo} size={CHECKLIST_ICON_SIZE} />
        {finishInstallUrl ? (
          <Link
            className={classes.checklistLink}
            href={finishInstallUrl}
            rel="noreferrer"
            target="_blank"
          >
            {finishLabel}
            <ExternalLink size={CHECKLIST_ICON_SIZE} />
          </Link>
        ) : (
          finishLabel
        )}
      </Typography>
      {!finishInstallUrl && Boolean(display.manualInstallHint) && (
        <Typography className={classes.checklistHint} color="secondary" variant="caption">
          {display.manualInstallHint}
        </Typography>
      )}
    </div>
  );
}

function getInstallDisplayName(install: ChannelInstallationView) {
  return install.displayName ?? install.externalAccountId;
}

/**
 * Names the exact connection being removed.
 *
 * A row can list several installations of one provider, all offering the same
 * "Disconnect" label, so the dialog has to say which one it means — the name
 * is what tells two Slack workspaces apart.
 */
function getDisconnectMessage(display: ProviderDisplay, install: ChannelInstallationView): string {
  const target = `the ${display.label} ${display.connectionNoun} ${getInstallDisplayName(install)}`;
  const stops = `Aline stops using ${target}.`;
  return display.disconnectDetail ? `${stops} ${display.disconnectDetail}` : stops;
}

interface RowActionProps {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView | null;
  isOrgAdmin: boolean;
  loading: boolean;
}

function RowAction({ display, finishInstallUrl, install, isOrgAdmin, loading }: RowActionProps) {
  if (loading) return null;
  if (install)
    return (
      <InstalledActions
        display={display}
        finishInstallUrl={finishInstallUrl}
        install={install}
        isOrgAdmin={isOrgAdmin}
      />
    );

  const { installCta, installLabel } = display;

  if (installCta.kind === 'comingSoon') {
    return <IntegrationActionButton disabled>Coming soon</IntegrationActionButton>;
  }

  /**
   * Shown but inert for non-admins rather than hidden: the integration exists
   * either way, and a missing row reads as "we don't offer this" instead of
   * "someone else has to do it". Uniform across providers, matching the single
   * admin check in `authorizeChannelInstallRequest`.
   */
  if (!isOrgAdmin) {
    return (
      <AdminGatedControl>
        <IntegrationActionButton disabled>{installLabel}</IntegrationActionButton>
      </AdminGatedControl>
    );
  }

  return (
    <IntegrationActionButton component="a" href={installCta.href}>
      {installLabel}
    </IntegrationActionButton>
  );
}

/** Wraps a disabled control so the reason it is disabled is discoverable on hover. */
function AdminGatedControl({ children }: { children: JSX.Element }) {
  return (
    <AlineTooltip arrow placement="top" title={MANAGE_CHANNELS_ADMIN_TOOLTIP}>
      <span>{children}</span>
    </AlineTooltip>
  );
}

function buildChannelInstallPath(provider: 'slack' | 'teams') {
  return link.ApiIntegrationsChatProviderInstall({ provider });
}

type InstalledAction =
  | {
      disabled: boolean;
      href: string | null;
      key: 'finishSetup';
      kind: 'link';
      label: string;
      target?: '_blank';
    }
  | { disabled: boolean; key: 'disconnect'; kind: 'button'; label: string; onClick: () => void };

function InstalledActions({
  display,
  finishInstallUrl,
  install,
  isOrgAdmin,
}: {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView;
  isOrgAdmin: boolean;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [confirming, setConfirming] = useState(false);
  const [disconnect, { loading: disconnecting }] = Apollo.useDisconnectChannelInstallationMutation({
    refetchQueries: [Apollo.GetChannelInstallationsDocument],
  });

  const onConfirmDisconnect = useCallback(async () => {
    try {
      await disconnect({ variables: { id: install.id } });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(`Could not disconnect ${getInstallDisplayName(install)}. Try again.`, {
        variant: 'error',
      });
    }
  }, [disconnect, enqueueSnackbar, install]);

  const availableActions: InstalledAction[] = [];
  if (getIsAwaitingProvider(install)) {
    const retryInstallUrl = display.installCta.kind === 'link' ? display.installCta.href : null;
    availableActions.push({
      disabled: !finishInstallUrl && (!isOrgAdmin || !retryInstallUrl),
      href: finishInstallUrl ?? (isOrgAdmin ? retryInstallUrl : null),
      key: 'finishSetup',
      kind: 'link',
      label: finishInstallUrl ? 'Finish setup' : 'Retry setup',
      target: finishInstallUrl ? '_blank' : undefined,
    });
  }
  availableActions.push({
    disabled: !isOrgAdmin || disconnecting,
    key: 'disconnect',
    kind: 'button',
    label: 'Disconnect',
    onClick: () => setConfirming(true),
  });
  const actionButtons = availableActions.map(action => (
    <InstalledActionButton key={action.key} action={action} />
  ));

  return (
    <div className={classes.action}>
      <InstalledActionGroup label={`${display.label} integration actions`}>
        {actionButtons}
      </InstalledActionGroup>
      <ConfirmationDialog
        message={getDisconnectMessage(display, install)}
        open={confirming}
        primaryText="Disconnect"
        title={`Disconnect ${display.label}?`}
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={onConfirmDisconnect}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}

function InstalledActionGroup({ children, label }: { children: JSX.Element[]; label: string }) {
  const { classes } = useStyles();
  if (children.length === 1) return children.at(0) ?? null;

  return (
    <div aria-label={label} className={classes.actionButtons} role="group">
      {children}
    </div>
  );
}

function InstalledActionButton({ action }: { action: InstalledAction }) {
  switch (action.kind) {
    case 'link':
      return (
        <IntegrationActionButton
          component="a"
          disabled={action.disabled}
          endIcon={<ChevronRight size={18} />}
          href={action.href ?? undefined}
          rel={action.target === '_blank' ? 'noreferrer' : undefined}
          target={action.target}
        >
          {action.label}
        </IntegrationActionButton>
      );
    case 'button':
      return (
        <IntegrationActionButton disabled={action.disabled} onClick={action.onClick}>
          {action.label}
        </IntegrationActionButton>
      );
  }
}
